import { ExportStatStrip } from "@/features/exports/components/ExportStatStrip";
import { useExportJobStats } from "@/features/exports/hooks/useExportJobStats";
import { ExportTableFoot } from "@/features/exports/components/ExportTableFoot";
import { ExportsEmptyState } from "@/features/exports/components/ExportsEmptyState";
import { ExportsTable } from "@/features/exports/components/ExportsTable";
import { ExportsTableToolbar } from "@/features/exports/components/ExportsTableToolbar";
import { useExportListFilters } from "@/features/exports/hooks/useExportListFilters";
import { useExportJobs } from "@/features/exports/hooks/useExportJobs";
import { DownloadListWorkspace } from "@/features/invoices/components/DownloadListWorkspace";
import { PageHeader } from "@/features/layout/components/PageHeader";

export function ExportsPage() {
  const {
    page,
    pageSize,
    filters,
    draft,
    isDirty,
    setPage,
    setPageSize,
    patchDraft,
    commitSearch,
    resetFilters,
  } = useExportListFilters();
  const { data, isLoading, error, isFetching } = useExportJobs(
    page,
    pageSize,
    filters,
  );
  const statsQuery = useExportJobStats(filters);

  const paged = data?.items ?? [];
  const hasMore = data?.has_more ?? false;
  const total = page * pageSize + paged.length + (hasMore ? 1 : 0);
  const showPager = !isLoading && !error && (paged.length > 0 || page > 0);

  return (
    <DownloadListWorkspace
      pinned={
        <>
          <PageHeader
            title="Export History"
            lead="Re-download ZIP files from past export jobs."
            breadcrumbSuffix="Export History"
            className="page-head-row--compact"
          />

          {statsQuery.data ? (
            <ExportStatStrip stats={statsQuery.data} />
          ) : statsQuery.isLoading ? (
            <p className="mb-2 text-sm text-[var(--muted)]">Loading stats…</p>
          ) : null}

          <div className="exports-list-toolbar-band">
            <ExportsTableToolbar
              pinned
              pageSize={pageSize}
              draft={draft}
              isDirty={isDirty}
              searchLoading={isFetching && !isLoading}
              onPageSizeChange={setPageSize}
              onDraftChange={patchDraft}
              onSearch={commitSearch}
              onClear={resetFilters}
            />
          </div>
        </>
      }
      table={
        <div className="panel exports-workspace">
          {isLoading ? (
            <p className="px-4 py-6 text-sm text-[var(--muted)]">Loading exports…</p>
          ) : null}
          {!isLoading && !error && paged.length === 0 ? (
            page === 0 && filters.status === "all" ? (
              <ExportsEmptyState />
            ) : (
              <p className="empty-state px-4 py-8 text-sm">
                No exports match these filters.{" "}
                <button
                  type="button"
                  className="link-quiet"
                  onClick={() => resetFilters()}
                >
                  Clear filters
                </button>
              </p>
            )
          ) : null}
          {!isLoading && !error && paged.length > 0 ? (
            <ExportsTable items={paged} embedded />
          ) : null}
        </div>
      }
      pager={
        showPager ? (
          <ExportTableFoot
            compact
            page={page}
            pageSize={pageSize}
            total={total}
            showing={paged.length}
            hasMore={hasMore}
            pageLoading={isFetching}
            onPageChange={setPage}
          />
        ) : null
      }
    />
  );
}
